import { Component, OnChanges, Output, Input, EventEmitter, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule, FormGroup, AbstractControl, FormArray } from '@angular/forms';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { GenericModalService, SelectMultiChoiceTypes, ModalSelectElement } from '../../shared/generic-modal/generic-modal.service';
import { TransportOutConfiguration, TransportOutConfigurationAndValues } from '../models/transport-configuration';
import { TransportType } from '../models/transport-type';
import { TransportService } from '../services/transport.service';
import { DataFieldGroup } from '../../shared/datafields/models/datafield-group';
import { Partner, PartnerSystem } from '../../partner/models';
import { PartnerSystemService } from '../../partner/services/partner-system.service';
import { MonitorGroup } from 'src/app/monitor/models/monitor-group';
import { Stakeholder } from 'src/app/stakeholder/models/stakeholder';
import { MonitorSchedule } from 'src/app/monitor/models/monitor-schedule';
import { MonitorService } from 'src/app/monitor/services/monitor.service';
import { StakeholderService } from 'src/app/stakeholder/services/stakeholder.service';
import { MonitorItem, MonitorItemClass } from 'src/app/monitor/models/monitor-item';
import { ArtifactAssemblyTypes } from 'src/app/developer/models/artifact';
import { MonitorItemEditComponent } from 'src/app/monitor/components/monitor-item-edit/monitor-item-edit.component';

import { PartnerSelectorComponent } from '../../partner/partner-selector.component';
import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';
import { MonitorItemEditComponent as MonitorItemEditComponent_1 } from '../../monitor/components/monitor-item-edit/monitor-item-edit.component';

@Component({
    selector: 'ep-transport-configuration-edit',
    templateUrl: './transport-configuration-edit.component.html',
    styleUrls: ['./transport-configuration-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, PartnerSelectorComponent, DataFieldGroupContainerComponent, MonitorItemEditComponent_1]
})
export class TransportConfigurationEditComponent implements OnChanges {
    @ViewChild(MonitorItemEditComponent) private monitorItemEdit: MonitorItemEditComponent = null;

    @Input() transportConfiguration: TransportOutConfiguration = null;
    @Input() partner: Partner = null;
    @Input() partnerRestrictedMode = false;
    @Input() readonly = false;

    @Output() saved: EventEmitter<TransportOutConfiguration> = new EventEmitter<TransportOutConfiguration>();
    @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();

    transportTypes: TransportType[] = [];
    transportConfigDataGroups: DataFieldGroup[] = [];
    partnerSystems: PartnerSystem[] = [];

    // State-specific properties
    clonedTransportConfiguration: TransportOutConfiguration = null;
    clonedPartner: Partner = null;

    // Object used to keep loaded environment-values
    canEdit = false;
    showError = false;
    currentTransportTypeSupportsMonitor = false;

    dataFieldsFormGroup = new UntypedFormGroup({});
    transportTypeFormGroup: UntypedFormGroup;

    monitorGroups: MonitorGroup[] = [];
    stakeholders: Stakeholder[] = [];
    monitorSchedules: MonitorSchedule[] = [];

    fcMonitorItem: UntypedFormControl;

    constructor(
        private transportService: TransportService,
        private partnerSystemService: PartnerSystemService,
        private modalService: GenericModalService,
        private loadingPanelService: LoadingPanelService,
        private monitorService: MonitorService,
        private stakeholderService: StakeholderService,
    ) {
        this.transportTypeFormGroup = new UntypedFormGroup({
            inputName: new UntypedFormControl('', Validators.required),
            transportTypeId: new UntypedFormControl(null, Validators.required),
            monitorItem: new UntypedFormControl(null),
        });

        this.fcMonitorItem = this.transportTypeFormGroup.get('monitorItem') as UntypedFormControl;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ngOnChanges(changes: any): void {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if ((changes.partner || changes.transportConfiguration) && this.transportConfiguration) {
            void this.refresh();
        }
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect(`Loading ${this.transportConfiguration.Name || 'transport types'}...`);

            // Reste all state-specific properties
            this.clonedTransportConfiguration = null;
            this.clonedPartner = null;

            [this.transportTypes,
            this.monitorGroups,
            this.monitorSchedules,
            this.stakeholders,
            this.partnerSystems,
            ] = await Promise.all([
                this.transportService.getTransportTypes(this.transportService.transportTypesOut).then(result => result.sort((a, b) => a.Name.localeCompare(b.Name))),
                this.monitorService.getMonitorGroups(),
                this.monitorService.getMonitorSchedules(),
                this.stakeholderService.getStakeholders(),
                this.partnerSystemService.getAllPartnerSystems(),
            ]);
            await this.loadDataForTransportConfiguration(this.transportConfiguration);

            // Clone the object so we can cancel any changes
            await this.resetClones();
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }

        await this.updateTransportTypeSupportsMonitor();
    }

    async resetClones(): Promise<void> {
        this.clonedTransportConfiguration = this.transportConfiguration ? { ...this.transportConfiguration } : null;

        if (this.clonedTransportConfiguration) {
            const hasMatchingTransportType = this.transportTypes
                .some(transportType => transportType.Id === this.clonedTransportConfiguration.TransportTypeId);

            if (!hasMatchingTransportType) {
                this.clonedTransportConfiguration.TransportTypeId = null;
            }
        }

        this.clonedPartner = this.partner ? { ...this.partner } : null;
        if (this.clonedTransportConfiguration && this.clonedPartner) {
            this.clonedTransportConfiguration.PartnerId = this.clonedPartner.PartnersId;
        }

        this.transportTypeFormGroup.patchValue({
            transportTypeId: this.clonedTransportConfiguration?.TransportTypeId ?? null,
        }, { emitEvent: false });

        await this.loadDataForTransportConfiguration(this.clonedTransportConfiguration);
    }

    async loadDataForTransportConfiguration(transportConfiguration: TransportOutConfiguration): Promise<void> {
        if (transportConfiguration.Id) {
            await this.loadDataFields(transportConfiguration);
        }
    }

    async loadDataFields(transportConfiguration: TransportOutConfiguration): Promise<void> {
        this.transportConfigDataGroups = [];
        if (transportConfiguration.TransportTypeId == null) {
            return;
        }
        try {
            this.transportConfigDataGroups = await this.transportService.getTransportDisplayGroups(transportConfiguration.TransportTypeId, transportConfiguration.Id);
        }
        catch { /* The error will be shown and logged via the generic error-handling */ }
    }

    async transportTypeChanged(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.updateMonitorItem(null);

            await Promise.all([
                this.loadDataFields(this.clonedTransportConfiguration),
                this.updateTransportTypeSupportsMonitor(),
            ]);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    async updateTransportTypeSupportsMonitor(): Promise<void> {
        this.currentTransportTypeSupportsMonitor = false;

        const artifactId = this.currentTransportTypeArtifactId;
        if (artifactId) {
            this.currentTransportTypeSupportsMonitor = await this.monitorService.getHasEmbeddedMonitorType(artifactId).toPromise();
            if (!this.currentTransportTypeSupportsMonitor) {
                this.updateMonitorItem(null);
            }
        }
    }

    async save() {
        this.dataFieldsFormGroup.updateValueAndValidity();
        this.transportTypeFormGroup.updateValueAndValidity();

        if (!this.dataFieldsFormGroup.valid || !this.transportTypeFormGroup.valid) {
            this.logInvalidControls();
            await this.modalService.messageDirect('Validation failed', 'Please fill out all required fields.');
            return;
        }

        try {
            this.loadingPanelService.showLoadingPanelDirect('Saving location...');

            const isNameAvailable = await this.transportService.isTransportOutConfigurationNameAvailable(
                this.clonedTransportConfiguration.PartnerId, this.clonedTransportConfiguration.Name, this.clonedTransportConfiguration.Id);

            if (!isNameAvailable) {
                this.loadingPanelService.hideLoadingPanel();
                await this.modalService.messageDirect('Name not available', 'The chosen name is not available. Please choose another one.');
                return;
            }

            const transportOutConfigurationAndValues: TransportOutConfigurationAndValues = {
                TransportConfiguration: this.clonedTransportConfiguration,
                DataFieldValues: DataFieldGroup.getDataFieldValues(this.transportConfigDataGroups, true),
                MonitorConfigValues: this.currentHasMonitor ? DataFieldGroup.getDataFieldValues(this.monitorItemEdit.artifactConfigDataFieldGroups, false) : null,
            };

            try {
                const savedTransportConfiguration = await this.transportService.saveTransportOutConfiguration(transportOutConfigurationAndValues);
                Object.assign(this.transportConfiguration, savedTransportConfiguration);
                this.saved.emit(this.transportConfiguration);
            }
            catch { /* The error will be shown and logged via the generic error-handling */ }
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    async cancel(): Promise<void> {
        await this.resetClones();
        this.cancelled.emit();
    }

    get allowPartnerChange(): boolean {
        return this.clonedTransportConfiguration && !this.clonedTransportConfiguration.Id;
    }

    partnerChanged(partner: Partner) {
        this.clonedPartner = partner;
        if (this.clonedTransportConfiguration) {
            this.clonedTransportConfiguration.PartnerId = partner?.PartnersId;
        }
    }

    get isGlobal(): boolean {
        return this.clonedTransportConfiguration && !this.clonedTransportConfiguration.PartnerId;
    }

    isGlobalChanged(isGlobal: boolean) {
        this.clonedPartner = isGlobal || !this.partner ? null : { ...this.partner };
        if (this.clonedTransportConfiguration) {
            this.clonedTransportConfiguration.PartnerId = this.clonedPartner?.PartnersId ?? null;
        }
    }

    partnerSystemChanged(partnerSystemId: number) {
        this.clonedTransportConfiguration.PartnerSystemId = partnerSystemId;
    }

    get currentHasMonitor(): boolean {
        return !!this.clonedTransportConfiguration?.MonitorItem;
    }

    addMonitor(): void {
        const monitorItem = new MonitorItemClass({
            name: this.clonedTransportConfiguration?.Name || '',
            embeddedTypeArea: ArtifactAssemblyTypes.TypeTransportSend,
            embeddedTypeId: this.clonedTransportConfiguration?.Id ?? 0,
        });

        this.updateMonitorItem(monitorItem);
    }

    removeMonitor(): void {
        this.updateMonitorItem(null);
    }

    updateMonitorItem(monitorItem: MonitorItem): void {
        this.transportTypeFormGroup.patchValue({ monitorItem });

        // This is necessary to prevent validation errors when there is no monitor
        if (monitorItem && this.transportTypeFormGroup.enabled) {
            this.fcMonitorItem.enable();
        }
        else {
            this.fcMonitorItem.disable();
        }

        if (this.clonedTransportConfiguration) {
            this.clonedTransportConfiguration.MonitorItem = monitorItem;
        }
    }

    get currentTransportTypeId(): number {
        return this.clonedTransportConfiguration ? this.clonedTransportConfiguration.TransportTypeId : -1;
    }

    get currentTransportType(): TransportType {
        const transportTypeId = this.currentTransportTypeId;
        return this.transportTypes.find(e => e.Id === transportTypeId);
    }

    get currentTransportTypeArtifactId(): string {
        const transportType = this.currentTransportType;
        const artifactId = transportType ? transportType.ArtifactId : null;
        return artifactId;
    }

    getSelectedTransportTypeArtifactId(): string {
        return this.currentTransportTypeArtifactId;
    }

    logInvalidControls() {
        type InvalidPair = { key: string; control: AbstractControl; path: string[]; };

        const collectInvalid = (ctrl: AbstractControl, key: string, path: string[] = []): InvalidPair[] => {
            const currentPath = [...path, key].filter(k => k !== '');
            // FormGroup
            if (ctrl instanceof UntypedFormGroup || ctrl instanceof FormGroup) {
                return Object.entries(ctrl.controls)
                    .flatMap(([childKey, childCtrl]) => collectInvalid(childCtrl, childKey, currentPath));
            }
            // FormArray
            if (ctrl instanceof FormArray) {
                return ctrl.controls
                    .flatMap((childCtrl, idx) => collectInvalid(childCtrl, String(idx), currentPath));
            }
            // Leaf
            return ctrl.invalid ? [{ key, control: ctrl, path: currentPath }] : [];
        };

        const invalidControls = [
            ...collectInvalid(this.dataFieldsFormGroup, ''),
            ...collectInvalid(this.transportTypeFormGroup, '')
        ];

        console.log('Invalid controls (key/control pairs):', invalidControls.map(i => ({ key: i.key, path: i.path.join('.'), errors: i.control.errors })));
    }
}
